import {useAppStore, useSnapshotStore, useUserStore} from '@/stores'
import useSound from './useSound'
import {ref} from 'vue'
import {mediator, messenger} from '@communication'
import useS3Service from './useS3Service'
import useBlob from './useBlob'
import useLocalStream from './useLocalStream'
import {snapshot} from '@/messages'
import {useRepository} from '.'
import {EventBus} from 'communication/src/EventBus'
import {sleep} from '@/utils'
import {useTranslation} from 'i18next-vue'

interface SnapshotCreatedEvent {
  image: string
  isThumbnail: boolean
}
interface SnapshotEvents {
  'snapshot-created': SnapshotCreatedEvent
}
const snapshotReject = ref<((reason?: any) => void) | null>(null)
const uploadedImage = ref<string | null>(null)
const loading = ref(false)
const installed = ref(false)
const snapshotEventBus = new EventBus<SnapshotEvents>()
export const useSnapshot = () => {
  const {playSound} = useSound(['camera'])
  const app = useAppStore()
  const user = useUserStore()
  const store = useSnapshotStore()
  const {t} = useTranslation()

  const install = () => {
    if (!installed.value) {
      installed.value = true
      messenger.subscribe('useSnapshot', 'strategy:data', handler)
    }
  }
  const uninstall = () => {
    messenger.subscribe('useSnapshot', 'strategy:data', handler)
  }
  const subscribe = (type: keyof SnapshotEvents, callback: EventListener) => {
    snapshotEventBus.on(type, callback)
  }
  const unsubscribe = (type: keyof SnapshotEvents, callback: EventListener) => {
    snapshotEventBus.off(type, callback)
  }
  const handler = async ({type}: {type: string}) => {
    const {downloadSnapshot, getTemporarySnapshotUploadLinks, uploadToS3} = useS3Service()
    switch (type) {
      case 'snapshot:take': {
        if (user.isClient) {
          playSound('camera')
          loading.value = true
          const {blobToBase64} = useBlob()
          try {
            // No async executor here: a throw inside an async executor never settles
            // the constructed promise, so a failed capture would hang the loader forever
            const {imageData, thumbnailData} = await new Promise<{
              imageData: string | Blob
              thumbnailData: string | Blob
            }>((resolve, reject) => {
              snapshotReject.value = reject
              const {getSnapshot} = useLocalStream()
              getSnapshot()
                .then(({data, thumbnailData}) => resolve({imageData: data, thumbnailData}))
                .catch(reject)
            })
            const image = imageData instanceof window.Blob ? await blobToBase64(imageData) : imageData
            const thumbnail = thumbnailData instanceof window.Blob ? await blobToBase64(thumbnailData) : thumbnailData
            if (app.state.matches('app.recording.on')) {
              saveToBoard(image)
            }
            app.transition('draw')
            snapshotEventBus.emit('snapshot-created', {image, isThumbnail: false})
            const {uploadLink, thumbnailUploadLink} = await getTemporarySnapshotUploadLinks()
            await uploadToS3(thumbnailUploadLink, thumbnail)
            mediator.broadcast(snapshot.downloadThumbnailMessage())
            await uploadToS3(uploadLink, image)
            mediator.broadcast(snapshot.downloadMessage())
          } catch (error) {
            console.error(`[snapshot:take] ${error}`)
            app.transition('LEAVE_DRAW')
          }
          app.loaded()
        }
        break
      }
      case 'snapshot:dismiss': {
        app.transition('LEAVE_DRAW')
        break
      }
      case 'snapshot:download-thumbnail': {
        console.log('downloadthumbnail')
        const image = await downloadSnapshot(true)
        uploadedImage.value = null
        app.transition('draw')
        snapshotEventBus.emit('snapshot-created', {isThumbnail: true, image})
        app.loading(
          t('loader.transferringSnapshot', {
            progress: 100,
          })
        )
        app.loaded()
        loading.value = false
        if (!user.isAssistant) {
          playSound('camera')
        }
        break
      }
      case 'snapshot:download': {
        const image = await downloadSnapshot()
        console.log('download')
        snapshotEventBus.emit('snapshot-created', {isThumbnail: false, image})
        break
      }
      case 'snapshot:cancel': {
        snapshotReject.value?.(new Error('Cancel'))
        app.transition('LEAVE_DRAW')
        app.loaded()
        snapshotReject.value = null
        break
      }
    }
  }
  const saveToBoard = async (data: string) => {
    // Called fire-and-forget, so it must never reject
    try {
      const {saveSnapshot} = useS3Service()
      const {sendWsEvent} = useRepository()
      const {key, bucket} = await saveSnapshot(data)
      sendWsEvent(snapshot.boardMessage({key, bucket}))
    } catch (error) {
      console.error('[snapshot:saveToBoard]', error)
    }
  }

  const cancelSnapshot = () => {
    mediator.broadcast(snapshot.cancelSnapshotMessage())
    if (user.isClient) {
      snapshotReject.value?.(new Error('Cancel'))
      app.loaded()
      snapshotReject.value = null
    }
    dismiss('snapshot')
  }

  const take = () => {
    const {sendWsEvent} = useRepository()
    playSound('camera')
    app.loading(t('loader.takingSnapshot'))
    store.setTakePictureTouch({
      isUpload: true,
      isSaveDone: true,
    })
    mediator.broadcast(snapshot.takeMessage())
    sendWsEvent(snapshot.takeMessage())
  }

  const dismiss = async (type: string) => {
    const {deleteSnapshot} = useS3Service()
    const {sendWsEvent} = useRepository()
    if (type) {
      app.loading(type === 'snapshot' ? t('loader.savingSnapshot') : t('loader.savingUploadedImage'))
    }
    mediator.broadcast(snapshot.dismissMessage())
    uploadedImage.value = null
    store.setTakePictureTouch({
      isSaveDone: false,
    })
    sendWsEvent(snapshot.dismissMessage())
    await deleteSnapshot()
    app.transition('LEAVE_DRAW')
    await sleep(1000)
    app.loaded()
  }

  // A save while the board shows only the placeholder would store the low-quality placeholder: wait for the full
  // image first, at most timeoutMs. Returns false when it did not arrive, the caller then saves what the board shows.
  const waitForFullImage = async (type = 'snapshot', timeoutMs = 20000) => {
    if (!store.isPlaceholder) return true
    app.loading(type === 'image' ? t('loader.savingUploadedImage') : t('loader.savingSnapshot'))
    const deadline = Date.now() + timeoutMs
    while (store.isPlaceholder && Date.now() < deadline && app.state.matches('app.view.meeting.draw')) {
      await sleep(100)
    }
    return !store.isPlaceholder
  }

  const generateThumbnail = async (data: string) => {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.onload = () => {
        const canvas = document.createElement('canvas')
        canvas.width = img.width
        canvas.height = img.height
        const ctx = canvas.getContext('2d')
        if (ctx == null) {
          reject()
          return
        }
        ctx.drawImage(img, 0, 0, img.width, img.height)
        const thumbnailData = canvas.toDataURL('image/jpeg', 0.1)
        resolve(thumbnailData)
      }
      img.onerror = reject
      img.src = data
    })
  }

  const drop = async (data: string) => {
    const {getTemporarySnapshotUploadLinks, uploadToS3} = useS3Service()
    // Same as take(): the image is not saved here, OCR saves what it reads on demand (MeetingBoard recognizeOcr)
    store.setTakePictureTouch({
      isUpload: true,
      isSaveDone: true,
    })
    // Generate thumbnail from base64 image data
    const thumbnail = await generateThumbnail(data)
    const {uploadLink, thumbnailUploadLink} = await getTemporarySnapshotUploadLinks()
    await uploadToS3(thumbnailUploadLink, thumbnail)
    mediator.broadcast(snapshot.downloadThumbnailMessage())
    app.transition('draw')
    app.loaded()
    // Textract reads JPEG and PNG, OCR of other formats falls back to the board export
    uploadedImage.value = /^data:image\/(jpeg|png);/.test(data) ? data : null
    snapshotEventBus.emit('snapshot-created', {isThumbnail: false, image: data})
    await uploadToS3(uploadLink, data)
    mediator.broadcast(snapshot.downloadMessage())
    if (app.state.matches('app.recording.on')) {
      saveToBoard(data)
    }
  }

  const save = async (data: string) => {
    try {
      const {saveSnapshot} = useS3Service()
      const {sendWsEvent} = useRepository()
      const {key, bucket} = await saveSnapshot(data)
      store.setKey(key)
      store.setTakePictureTouch({
        isSaveDone: true,
      })
      sendWsEvent(snapshot.saveMessage({key, bucket}))
    } catch (error) {
      console.error(error)
    }
  }

  // Saves the dropped or uploaded image as is for OCR. No event is sent, so it is not added to the media
  // library. Rejects on failure, so OCR never reads the key of a previous image.
  const saveUploadedImage = async () => {
    const {saveSnapshot} = useS3Service()
    const {key} = await saveSnapshot(uploadedImage)
    store.setKey(key)
  }
  return {
    install,
    uninstall,
    loading,
    subscribe,
    unsubscribe,
    saveUploadedImage,
    save,
    cancelSnapshot,
    take,
    drop,
    dismiss,
    uploadedImage,
    waitForFullImage,
  }
}
